import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

// Generic filter bar for any list page backed by query params (Unidades,
// Partes, ...). The page says WHICH filters exist; this component owns their
// state and reports the resulting params, and the page fetches with them:
//
//   <TableFilters
//     search={{ param: 'buscar', placeholder: 'Código o proyecto' }}
//     filters={[
//       { param: 'estado_actual', label: 'Estado', type: 'select',
//         options: ['Operativo', { value: 'Baja', label: 'De baja' }] },
//       { param: 'codigo', label: 'Código', type: 'text' },
//     ]}
//     onChange={params => getUnidades(params).then(setRows)}
//   />
//
// `onChange` gets only the params that have a value (never `x=''`), so it can
// be handed straight to axios. It fires once on mount with `initial` (or {}),
// right away when a select changes, and `debounceMs` after typing stops in a
// text box or the search -- one request per pause, not one per keystroke.
export default function TableFilters({ filters = [], search, onChange, initial = {}, debounceMs = 300 }) {
  const { t } = useTranslation('common')
  const [values, setValues] = useState(initial)
  const timer = useRef(null)
  // Latest onChange without re-running effects when the parent re-renders
  // with a new inline function.
  // Refreshed after each render (not during it); declared before the mount
  // effect below, so it is already current when that one first emits.
  const onChangeRef = useRef(onChange)
  useEffect(() => { onChangeRef.current = onChange })

  const emit = (next) => {
    const params = Object.fromEntries(
      Object.entries(next)
        .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
        .filter(([, v]) => v !== '' && v != null)
    )
    onChangeRef.current?.(params)
  }

  // Once, on mount, with the starting values.
  useEffect(() => {
    emit(values)
    return () => clearTimeout(timer.current)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (param, value, { debounce }) => {
    const next = { ...values, [param]: value }
    setValues(next)
    clearTimeout(timer.current)
    if (debounce) timer.current = setTimeout(() => emit(next), debounceMs)
    else emit(next)
  }

  const clear = () => {
    clearTimeout(timer.current)
    setValues({})
    emit({})
  }

  const hasAny = Object.values(values).some(v => v !== '' && v != null)
  const inputCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

  return (
    <div className="flex gap-3 flex-wrap items-center">
      {search && (
        <div className="relative flex-1 min-w-56 max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input type="search" className={`${inputCls} w-full pl-9`}
            placeholder={search.placeholder || t('filter.search')}
            value={values[search.param] ?? ''}
            onChange={e => set(search.param, e.target.value, { debounce: true })} />
        </div>
      )}
      {filters.map(f => f.type === 'select' ? (
        <select key={f.param} className={inputCls} aria-label={f.label}
          value={values[f.param] ?? ''}
          onChange={e => set(f.param, e.target.value, { debounce: false })}>
          <option value="">{t('filter.all', { label: f.label })}</option>
          {(f.options || []).map(o => {
            const value = typeof o === 'string' ? o : o.value
            const label = typeof o === 'string' ? o : o.label
            return <option key={value} value={value}>{label}</option>
          })}
        </select>
      ) : (
        <input key={f.param} type="text" className={`${inputCls} w-40`}
          placeholder={f.label} aria-label={f.label}
          value={values[f.param] ?? ''}
          onChange={e => set(f.param, e.target.value, { debounce: true })} />
      ))}
      {hasAny && (
        <button type="button" onClick={clear}
          className="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors">
          {t('filter.clear')}
        </button>
      )}
    </div>
  )
}
